// src/app/services/login-auth.service.ts

import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { LoginRequest, LoginResponse, SessionData, UsuarioEstatico } from '../interfaces/login-auth.interface';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root'
})
export class LoginAuthService {

  private readonly SESSION_KEY = '_session_cpanel_cpeselva';
  private readonly SECRET_KEY = 'CPESELVA_SECRET_KEY_2024'; // Puedes cambiar esto

  // Datos estáticos de usuarios de prueba
  private usuariosEstaticos: UsuarioEstatico[] = [
    {
      ruc: '20123456789',
      usuario: 'admin',
      clave: 'Admin123!',
      nombreCompleto: 'Administrador Principal',
      tipo: 'ADMIN'
    },
    {
      ruc: '20123456789',
      usuario: 'usuario1',
      clave: 'User123!',
      nombreCompleto: 'Usuario Test 1',
      tipo: 'USER'
    },
    {
      ruc: '20987654321',
      usuario: 'empresa2',
      clave: 'Empresa123!',
      nombreCompleto: 'Empresa Test 2',
      tipo: 'USER'
    }
  ];

  constructor() {}

  /**
   * Login con RUC, usuario y contraseña (Datos estáticos)
   */
  login(loginData: LoginRequest): Observable<LoginResponse> {
    // Validar credenciales contra datos estáticos
    const usuarioValido = this.usuariosEstaticos.find(
      user =>
        user.ruc === loginData.ruc &&
        user.usuario === loginData.usuario &&
        user.clave === loginData.clave
    );

    if (usuarioValido) {
      // Generar token y sesión
      const sessionData: SessionData = {
        ruc: usuarioValido.ruc,
        username: usuarioValido.usuario,
        nombreCompleto: usuarioValido.nombreCompleto,
        tipo: usuarioValido.tipo,
        token: this.generateToken(),
        expiration: this.generateExpiration()
      };

      // Guardar sesión encriptada
      this.saveSession(sessionData);

      return of({
        success: true,
        message: 'Login exitoso',
        data: sessionData
      }).pipe(delay(500)); // Simular delay de red
    } else {
      return throwError(() => ({
        success: false,
        message: 'RUC, usuario o contraseña incorrectos'
      })).pipe(delay(500));
    }
  }

  /**
   * Logout - Eliminar sesión
   */
  logout(): void {
    localStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Verificar si existe sesión activa
   */
  isAuthenticated(): boolean {
    const session = this.getStoredSession();
    if (!session) return false;

    // Verificar si la sesión no ha expirado
    const expiration = new Date(session.expiration).getTime();
    const now = Date.now();

    return expiration > now;
  }

  /**
   * Obtener sesión actual
   */
  getCurrentSession(): SessionData | null {
    return this.getStoredSession();
  }

  /**
   * Generar token aleatorio
   */
  private generateToken(): string {
    const randomString = Math.random().toString(36).substring(2, 15) +
                         Math.random().toString(36).substring(2, 15);
    return CryptoJS.SHA256(randomString).toString();
  }

  /**
   * Generar fecha de expiración (2 horas desde ahora)
   */
  private generateExpiration(): string {
    const now = new Date();
    const expiration = new Date(now.getTime() + (2 * 60 * 60 * 1000)); // 2 horas
    return expiration.toISOString();
  }

  /**
   * Guardar sesión encriptada en localStorage
   */
  private saveSession(data: SessionData): void {
    const jsonString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, this.SECRET_KEY).toString();
    localStorage.setItem(this.SESSION_KEY, encrypted);
  }

  /**
   * Obtener sesión desencriptada de localStorage
   */
  private getStoredSession(): SessionData | null {
    const encryptedSession = localStorage.getItem(this.SESSION_KEY);
    if (!encryptedSession) return null;

    try {
      const bytes = CryptoJS.AES.decrypt(encryptedSession, this.SECRET_KEY);
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedData);
    } catch (error) {
      console.error('Error al desencriptar sesión:', error);
      return null;
    }
  }

  /**
   * Obtener lista de usuarios estáticos (para debug/testing)
   */
  getUsuariosEstaticos(): UsuarioEstatico[] {
    return this.usuariosEstaticos;
  }
}
